import React from 'react'
import { match } from 'ts-pattern'
import { FilterSchema } from '@adc/blueprint'
import type { PanelHeight } from '../types'
import Text from '../../text'
import Icon from '../../icon'
import SingleSelect from '../components/singleSelect'
import MultiSelect from '../components/multiSelect'
import InlineSelect from '../components/inlineSelect'
import Range from '../components/range'
import Boolean from '../components/boolean'
import { useBreakpoints } from '../../../../hooks'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

interface RenderControlProps {
  elmId: string
  filterDefinition: FilterSchema.FilterDefinition
  onChange: (key: string, value: FilterSchema.ValueType) => void
  value: FilterSchema.ValueType
}

const RenderControl = ({
  elmId,
  filterDefinition,
  onChange,
  value,
}: RenderControlProps) => {
  const breakpoint = useBreakpoints()
  const isMobile = breakpoint === 'mobile'

  const controlType: FilterSchema.ControlType = filterDefinition.controlType

  return match(controlType)
    .with('singleSelect', () => (
      <SingleSelect
        compact={isMobile}
        value={value as FilterSchema.SingleSelectValueType}
        onChange={onChange}
        elmId={elmId}
        definition={
          filterDefinition as FilterSchema.FilterSingleSelectDefinition
        }
      />
    ))
    .with('multiSelect', () => (
      <MultiSelect
        compact={isMobile}
        value={value as FilterSchema.MultiSelectValueType}
        onChange={onChange}
        elmId={elmId}
        definition={
          filterDefinition as FilterSchema.FilterMultiSelectDefinition
        }
      />
    ))
    .with('inlineSelect', () => (
      <InlineSelect
        compact={isMobile}
        value={value as FilterSchema.InlineSelectValueType}
        onChange={onChange}
        elmId={elmId}
        definition={
          filterDefinition as FilterSchema.FilterInlineSelectDefinition
        }
      />
    ))
    .with('range', () => (
      <Range
        compact={isMobile}
        value={
          value as FilterSchema.RangeValueType<FilterSchema.RangeControlValueType>
        }
        onChange={onChange}
        elmId={elmId}
        definition={filterDefinition as FilterSchema.FilterRangeDefinition}
      />
    ))
    .with('boolean', () => (
      <Boolean
        compact={isMobile}
        value={value as FilterSchema.BooleanValueType}
        onChange={onChange}
        elmId={elmId}
        definition={filterDefinition as FilterSchema.FilterBooleanDefinition}
      />
    ))
    .otherwise(() => {
      throw new Error('Invalid control type')
    })
}

interface LabelProps {
  description?: FilterSchema.FilterDefinition['description']
  label?: FilterSchema.FilterDefinition['label']
  labelIconProps?: FilterSchema.FilterDefinition['labelIconProps']
  noMargin?: boolean
}

const Label = ({
  description,
  label,
  labelIconProps,
  noMargin,
}: LabelProps) => (
  <div className={cx('label-and-description', { 'no-margin-label': noMargin })}>
    <div className={cx('label-container')}>
      {typeof labelIconProps?.name === 'string' && (
        // @ts-ignore TS being stupid
        <Icon fitted iconStyle='regular' size='medium' {...labelIconProps} />
      )}
      {/* If the filter has a short label, use that, otherwise use the label */}
      <Text.Body size='large' weight='heavy' className={cx('filter-label')}>
        {label && label}
      </Text.Body>
    </div>
    {description && (
      <Text color='gray' size='tiny' className={cx('description')}>
        {description as React.ReactNode}
      </Text>
    )}
  </div>
)

type SingleFilterProps = {
  displayTitle?: boolean
  elmId?: string
  filterDefinition: FilterSchema.FilterDefinition
  value: FilterSchema.ValueType
  onChange: (key: string, value: FilterSchema.ValueType) => void
}

const SingleFilterLayout: React.FunctionComponent<SingleFilterProps> = ({
  displayTitle = true,
  elmId = 'filter_control',
  filterDefinition,
  value,
  onChange,
}) => {
  return (
    <div
      data-elm-id={elmId}
      className={cx('filter-layout', {
        half:
          filterDefinition.styleIntent?.width === 'half' &&
          filterDefinition.controlType === 'range',
        'full-height': filterDefinition.styleIntent?.height === 'full',
      })}
    >
      {/* boolean control we'll handle label and description in the control */}
      {filterDefinition.controlType !== 'boolean' && displayTitle && (
        <Label {...filterDefinition} />
      )}
      <div className={cx('single-filter')}>
        <RenderControl
          filterDefinition={filterDefinition}
          elmId={elmId}
          onChange={onChange}
          value={value}
        />
      </div>
    </div>
  )
}

type FilterGroupLayoutProps = {
  elmId?: string
  groupDefinition: FilterSchema.FilterGroupDefinition
  groupData: FilterSchema.FilterGroupViewModel
  noPadding?: boolean
  onChange: (
    // modelName: string,
    key: string,
    value: FilterSchema.ValueType
  ) => void
  height?: PanelHeight
}

/**
 * Renders a group of filters
 * @param groupDefinition: the definition for the group
 * @param groupData: the data for the group
 */
const FilterGroupLayout: React.FunctionComponent<FilterGroupLayoutProps> = ({
  elmId = 'filter_panel',
  groupDefinition,
  groupData,
  noPadding = false,
  onChange,
  height,
}) => {
  const onChangeFilter = (key: string, newValue: FilterSchema.ValueType) => {
    onChange(key, newValue)
  }
  return (
    <div
      data-elm-id={elmId}
      className={cx('layout', {
        'full-height': height === 'max',
        'no-padding': noPadding,
      })}
    >
      <div
        className={cx('filter-layout-items', {
          compact: groupDefinition.styleIntent?.compact,
        })}
      >
        {groupDefinition.items.map((item, index) => {
          if (item.itemType === 'filter') {
            const filterDefinition = item as FilterSchema.FilterDefinition
            const name = filterDefinition.name
            return (
              <SingleFilterLayout
                displayTitle={filterDefinition.styleIntent?.displayTitle}
                key={index}
                filterDefinition={filterDefinition}
                value={groupData[name]}
                onChange={onChangeFilter}
              />
            )
          } else if (item.itemType === 'section') {
            // section rendering
            const sectionDefinition =
              item as FilterSchema.FilterSectionDefinition
            return (
              <div
                key={index}
                className={cx('section', {
                  compact: sectionDefinition.styleIntent?.compact,
                  split: sectionDefinition.styleIntent?.twoColumns === 'all',
                  'desktop-split':
                    sectionDefinition.styleIntent?.twoColumns ===
                    'desktop-only',
                })}
              >
                {sectionDefinition.label && (
                  <Label
                    {...sectionDefinition}
                    noMargin={!!sectionDefinition.styleIntent?.twoColumns}
                  />
                )}
                {sectionDefinition.filters.map((item, index) => {
                  const filterDefinition = item
                  const name = filterDefinition.name
                  return (
                    <SingleFilterLayout
                      displayTitle={filterDefinition.styleIntent?.displayTitle}
                      filterDefinition={filterDefinition}
                      key={index}
                      onChange={onChangeFilter}
                      value={groupData[name]}
                    />
                  )
                })}
                {sectionDefinition.styleIntent?.showDivider &&
                  !sectionDefinition.label &&
                  !sectionDefinition.description && (
                    <hr className={cx('divider')} />
                  )}
              </div>
            )
          } else {
            throw new Error(
              `Invalid group item type: ${groupDefinition.itemType}.  Expected 'filter' or 'section'.`
            )
          }
        })}
      </div>
    </div>
  )
}

export default FilterGroupLayout
